import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { APP_ID } from "../config/constants";

export interface AuditLogEntry {
  id?: string;
  action:
    | "CREATE"
    | "UPDATE"
    | "DELETE"
    | "RESTORE"
    | "STATUS_CHANGE"
    | "LOGIN"
    | "INVOICE_SENT";
  target: {
    collection: string;
    id: string;
    name?: string; // e.g., Case Client Name
  };
  actor: {
    uid: string;
    email: string;
  };
  diff?: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  metadata?: any;
  timestamp: any;
}

// const AUDIT_COLLECTION = "global_audit_logs"; // Removed unused constant

/**
 * Logs a critical action to the global audit trail.
 */
export const logAuditAction = async (
  action: AuditLogEntry["action"],
  target: AuditLogEntry["target"],
  actor: AuditLogEntry["actor"],
  diff?: AuditLogEntry["diff"],
  metadata?: any,
) => {
  try {
    const logsRef = collection(db, "artifacts", APP_ID, "audit_logs");

    await addDoc(logsRef, {
      action,
      target,
      actor,
      diff: diff || null,
      metadata: metadata || null,
      timestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error("Failed to write audit log:", error);
  }
};

/**
 * Fetches all audit log entries for a specific case, ordered chronologically.
 */
export const getAuditLogsForCase = async (caseId: string): Promise<AuditLogEntry[]> => {
  try {
    const logsRef = collection(db, "artifacts", APP_ID, "audit_logs");
    // Only filter by target.id to avoid needing a composite index
    const q = query(logsRef, where("target.id", "==", caseId));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a: any, b: any) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0)) as AuditLogEntry[];
  } catch (error) {
    console.error("Error fetching case audit logs:", error);
    return [];
  }
};

/**
 * Fetches recent audit logs.
 */
export const getAuditLogs = async (limitCount = 100) => {
  try {
    const logsRef = collection(db, "artifacts", APP_ID, "audit_logs");
    const q = query(logsRef, orderBy("timestamp", "desc"), limit(limitCount));

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as AuditLogEntry[];
  } catch (error) {
    console.error("Error fetching audit logs:", error);
    return [];
  }
};
